import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import type { Product, ProductDetail } from "../types/product";
import type { ShopifyProductNode } from "../types/shopify";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

function extractNumericId(gid: string): string {
  return gid.split("/").pop() || gid;
}

function deriveCategory(node: ShopifyProductNode): Product["category"] {
  const tags = node.tags.map((t) => t.toLowerCase());
  const type = (node.productType || "").toLowerCase();
  const title = node.title.toLowerCase();

  if (
    type.includes("gas") ||
    type.includes("fuel") ||
    tags.some((t) => t.includes("gas") || t.includes("fuel") || t.includes("propane")) ||
    title.includes("gas generator") ||
    title.includes("dual fuel") ||
    title.includes("portable generator")
  ) {
    return "gas";
  }

  if (
    type.includes("standby") ||
    type.includes("whole-home") ||
    type.includes("whole home") ||
    tags.some((t) => t.includes("standby") || t.includes("whole home")) ||
    title.includes("standby") ||
    title.includes("whole home")
  ) {
    return "whole-home";
  }

  return "solar";
}

const BEST_SELLER_TAGS = ["best-seller", "bestseller", "best seller", "top-seller", "topseller", "popular", "#1", "top rated", "top-rated"];
const NEW_ARRIVAL_TAGS = ["new", "new-arrival", "new arrival", "just-in", "just in", "newly added", "new product", "new item"];
const LIMITED_STOCK_QTY_THRESHOLD = 8;

function detectBadges(node: ShopifyProductNode): {
  isBestSeller: boolean;
  isNew: boolean;
  isLimitedStock: boolean;
} {
  const tags = node.tags.map((t) => t.toLowerCase().trim());

  const isBestSeller = BEST_SELLER_TAGS.some((kw) => tags.some((t) => t.includes(kw)));
  const isNew = NEW_ARRIVAL_TAGS.some((kw) => tags.some((t) => t.includes(kw)));

  const totalQty = node.variants.edges.reduce((sum, e) => {
    const qty = e.node.quantityAvailable;
    return sum + (typeof qty === "number" ? qty : 0);
  }, 0);
  const hasQtyData = node.variants.edges.some((e) => typeof e.node.quantityAvailable === "number");
  const isLimitedStock =
    node.availableForSale &&
    hasQtyData &&
    totalQty > 0 &&
    totalQty <= LIMITED_STOCK_QTY_THRESHOLD;

  return { isBestSeller, isNew, isLimitedStock };
}

function shopifyNodeToProduct(node: ShopifyProductNode): Product {
  const id = extractNumericId(node.id);
  const price = parseFloat(node.priceRange.minVariantPrice.amount);
  const compareAtRaw = parseFloat(node.compareAtPriceRange?.minVariantPrice?.amount || "0");
  const originalPrice = compareAtRaw > price && compareAtRaw > 0 ? compareAtRaw : undefined;

  const highlights = node.tags
    .filter((t) => t.length > 3 && t.length < 60 && !t.includes(":"))
    .slice(0, 4);

  const variantId = node.variants.edges[0]?.node.id || undefined;
  const { isBestSeller, isNew, isLimitedStock } = detectBadges(node);

  return {
    id,
    variantId,
    name: node.title,
    brand: node.vendor,
    category: deriveCategory(node),
    price,
    originalPrice,
    capacity: "",
    watts: "",
    rating: 4.7,
    reviews: 0,
    highlights,
    image: node.images.edges[0]?.node.url || "",
    inStock: node.availableForSale,
    badge: isBestSeller ? "Best Seller" : undefined,
    isNew,
    isLimitedStock,
  };
}

function shopifyNodeToProductDetail(node: ShopifyProductNode, productId: string): ProductDetail {
  const images = node.images.edges.map((e) => e.node.url);

  const specs: { label: string; value: string }[] = [
    { label: "Brand", value: node.vendor },
    { label: "Availability", value: node.availableForSale ? "In Stock" : "Out of Stock" },
  ];

  if (node.productType) {
    specs.push({ label: "Product Type", value: node.productType });
  }

  const firstVariant = node.variants.edges[0]?.node;
  if (firstVariant) {
    specs.push({
      label: "Price",
      value: `$${parseFloat(firstVariant.price.amount).toLocaleString()}`,
    });
    if (firstVariant.quantityAvailable !== null && firstVariant.quantityAvailable !== undefined) {
      specs.push({ label: "Qty Available", value: `${firstVariant.quantityAvailable} units` });
    }
  }

  if (node.variants.edges.length > 1) {
    specs.push({
      label: "Available Options",
      value: node.variants.edges.map((e) => e.node.title).join(", "),
    });
  }

  const typeLC = (node.productType || "").toLowerCase();
  const isBattery = typeLC.includes("solar") || typeLC.includes("battery") || typeLC.includes("power station");

  const fallbackImage =
    "https://public.readdy.ai/ai/img_res/ccd76bd1841a9ca89f194023f9a906a2.jpg";
  const paddedImages = images.length > 0
    ? [...images, ...Array(Math.max(0, 4 - images.length)).fill(images[0])]
    : [fallbackImage, fallbackImage, fallbackImage, fallbackImage];

  return {
    id: productId,
    description:
      node.description ||
      `The ${node.title} from ${node.vendor} is a professional-grade backup power solution engineered for reliability when you need it most. Backed by ${node.vendor}'s industry-leading engineering and Iron Haven Supply's expert curation.`,
    batteryType: isBattery ? "LFP (Lithium Iron Phosphate)" : undefined,
    weight: "See product documentation",
    dimensions: "See product documentation",
    chargeTime: isBattery ? "See product documentation" : undefined,
    warranty: "2 Years Limited",
    specs,
    whatsInBox: ["Main Unit (1)", "Cables & Accessories", "User Manual", "Warranty Documentation"],
    images: paddedImages,
    reviews: [
      {
        id: `r${productId}a`,
        author: "Verified Buyer",
        location: "United States",
        rating: 5,
        date: "Feb 2025",
        title: "Excellent product, exactly as described",
        body: "Very happy with this purchase. Arrived quickly and works exactly as described. Iron Haven had great customer service when I had a question during setup.",
        verified: true,
      },
      {
        id: `r${productId}b`,
        author: "Satisfied Customer",
        location: "United States",
        rating: 4,
        date: "Jan 2025",
        title: "Great quality, highly recommend",
        body: "Build quality is impressive. Already recommended it to family members. Does exactly what it promises and ships fast.",
        verified: true,
      },
    ],
  };
}

interface ShopifyProductsContextValue {
  products: Product[];
  productNodes: Record<string, ShopifyProductNode>;
  loading: boolean;
  error: string | null;
  getProductById: (id: string) => Product | undefined;
  getProductDetailById: (id: string) => ProductDetail | undefined;
}

const ShopifyProductsContext = createContext<ShopifyProductsContextValue | null>(null);

export function ShopifyProductsProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [productNodes, setProductNodes] = useState<Record<string, ShopifyProductNode>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchProducts() {
      try {
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/shopify-shop-products`,
          {
            headers: {
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.error) throw new Error(data.error);

        if (!cancelled) {
          const nodes = (data.products || []) as ShopifyProductNode[];
          const nodesMap: Record<string, ShopifyProductNode> = {};
          const prods: Product[] = [];

          for (const node of nodes) {
            if (!node) continue;
            const id = extractNumericId(node.id);
            nodesMap[id] = node;
            prods.push(shopifyNodeToProduct(node));
          }

          setProductNodes(nodesMap);
          setProducts(prods);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load products");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  const getProductById = useCallback(
    (id: string) => products.find((p) => p.id === id),
    [products]
  );

  const getProductDetailById = useCallback(
    (id: string) => {
      const node = productNodes[id];
      if (!node) return undefined;
      return shopifyNodeToProductDetail(node, id);
    },
    [productNodes]
  );

  return (
    <ShopifyProductsContext.Provider
      value={{ products, productNodes, loading, error, getProductById, getProductDetailById }}
    >
      {children}
    </ShopifyProductsContext.Provider>
  );
}

export function useShopifyProducts() {
  const ctx = useContext(ShopifyProductsContext);
  if (!ctx) throw new Error("useShopifyProducts must be used within ShopifyProductsProvider");
  return ctx;
}
